import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Autocomplete,
  Collapse,
  useTheme,
  alpha,
} from "@mui/material";
import { useForm } from "@epam/uui";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Country } from "../../../dialog/couriers/components/LocationForm/LocationForm";
import isOther from "../../utils/isOther";

export default function FilterBar({ onSubmit, countryList, sizeList }: any) {
  const theme = useTheme();
  const [showFilters, setShowFilters] = useState(false);

  const courierTypes = [
    { id: "COURIER", type: "Courier" },
    { id: "SENDER", type: "Sender" },
  ];

  const { lens, save } = useForm<any>({
    value: {},
    onSave: ({
      courierType,
      size,
      sourceCountry,
      destinationCountry,
      sourceCity,
      destinationCity,
      sourceCountryCustom,
      sourceCityCustom,
      destinationCountryCustom,
      destinationCityCustom,
    }) => {
      onSubmit({
        courierType,
        size,
        sourceCountry: isOther(sourceCountry?.name)
          ? sourceCountryCustom
          : sourceCountry?.name,
        sourceCity: isOther(sourceCity?.name)
          ? sourceCityCustom
          : sourceCity?.name,
        destinationCountry: isOther(destinationCountry?.name)
          ? destinationCountryCustom
          : destinationCountry?.name,
        destinationCity: isOther(destinationCity?.name)
          ? destinationCityCustom
          : destinationCity?.name,
      });
      return Promise.resolve({});
    },
    beforeLeave: null,
  });

  const onClear = () => {
    lens.set({});
    onSubmit({});
  };

  const getCityList = (cityType: string) => {
    const country = lens.prop(cityType).toProps().value;
    const cityList = countryList.filter(
      (c: Country) => c.countryCode === country?.id
    )?.[0];
    return cityList?.cityList || [];
  };

  const sourceCityList = useMemo(
    () => getCityList("sourceCountry"),
    [lens.prop("sourceCountry").get(), countryList]
  );

  const destinationCityList = useMemo(
    () => getCityList("destinationCountry"),
    [lens.prop("destinationCountry").get(), countryList]
  );

  // Calculate active filter count for mobile badge
  const activeFilterCount = useMemo(() => {
    const filters = lens.get();
    return Object.values(filters).filter(
      (value) => value != null && value !== ""
    ).length;
  }, [lens.get()]);

  // Add "Other" option to country/city lists
  const getOptionsWithOther = (options: any[] = []) => {
    if (options.some((o) => isOther(o.name) || o.id === "other")) {
      return options;
    }
    return [...options, { id: "other", name: "Other" }];
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.editorial.paperGray}`,
      }}
    >
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={<FilterListIcon />}
          endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            mb: 2,
            py: 1.5,
            minHeight: 48,
            borderColor: theme.palette.courier.main,
            color: theme.palette.courier.dark,
            "&:hover": {
              borderColor: theme.palette.courier.dark,
              backgroundColor: alpha(theme.palette.courier.main, 0.04),
            },
          }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
          {activeFilterCount > 0 && ` (${activeFilterCount})`}
        </Button>

      {/* Filter fields - collapsible on mobile */}
      <Collapse in={showFilters} timeout={400}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
            gap: { xs: 2, md: 3, lg: 4 },
            mb: 2,
          }}
        >
          {/* Type Filter */}
          <Autocomplete
            value={
              courierTypes.find(
                (t) => t.id === lens.prop("courierType").get()
              ) || null
            }
            onChange={(_, newValue) =>
              lens.prop("courierType").set(newValue?.id)
            }
            options={courierTypes}
            getOptionLabel={(option) => option.type}
            renderInput={(params) => (
              <TextField {...params} label="Type" placeholder="Select type" />
            )}
            disableClearable={false}
          />

          {/* Size Filter */}
          <Autocomplete
            value={
              sizeList.find((s: any) => s.id === lens.prop("size").get()) ||
              null
            }
            onChange={(_, newValue) => lens.prop("size").set(newValue?.id)}
            options={sizeList}
            getOptionLabel={(option) => option.name || ""}
            renderInput={(params) => (
              <TextField {...params} label="Size" placeholder="Select size" />
            )}
            disableClearable={false}
          />

          {/* From Country Filter */}
          <Box>
            <Autocomplete
              value={lens.prop("sourceCountry").get() || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => {
                lens.prop("sourceCountry").set(newValue);
                lens.prop("sourceCity").set(null);
              }}
              options={getOptionsWithOther(countryList)}
              getOptionLabel={(option) => option.name || ""}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props as any;
                return (
                  <Box
                    component="li"
                    {...otherProps}
                    key={key || option.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {!isOther(option.name) && option.flagIconUrl && (
                      <img
                        src={`https://${option.flagIconUrl}`}
                        alt=""
                        style={{ width: 24, height: 16, objectFit: "cover" }}
                      />
                    )}
                    <span>{option.name}</span>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From Country"
                  placeholder="Select country"
                />
              )}
              disableClearable={false}
            />
            {isOther(lens.prop("sourceCountry").get()?.id) && (
              <Collapse in={isOther(lens.prop("sourceCountry").get()?.id)}>
                <TextField
                  label="Specify source country"
                  value={lens.prop("sourceCountryCustom").get() || ""}
                  onChange={(e) =>
                    lens.prop("sourceCountryCustom").set(e.target.value)
                  }
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </Collapse>
            )}
          </Box>

          {/* From City Filter */}
          <Box>
            <Autocomplete
              value={lens.prop("sourceCity").get() || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => {
                lens.prop("sourceCity").set(newValue);
              }}
              options={getOptionsWithOther(sourceCityList)}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From City"
                  placeholder="Select city"
                />
              )}
              disableClearable={false}
              disabled={!lens.prop("sourceCountry").get()}
            />
            {isOther(lens.prop("sourceCity").get()?.id) && (
              <Collapse in={isOther(lens.prop("sourceCity").get()?.id)}>
                <TextField
                  label="Specify source city"
                  value={lens.prop("sourceCityCustom").get() || ""}
                  onChange={(e) =>
                    lens.prop("sourceCityCustom").set(e.target.value)
                  }
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </Collapse>
            )}
          </Box>

          {/* To Country Filter */}
          <Box>
            <Autocomplete
              value={lens.prop("destinationCountry").get() || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => {
                lens.prop("destinationCountry").set(newValue);
                lens.prop("destinationCity").set(null);
              }}
              options={getOptionsWithOther(countryList)}
              getOptionLabel={(option) => option.name || ""}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props as any;
                return (
                  <Box
                    component="li"
                    {...otherProps}
                    key={key || option.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {!isOther(option.name) && option.flagIconUrl && (
                      <img
                        src={`https://${option.flagIconUrl}`}
                        alt=""
                        style={{ width: 24, height: 16, objectFit: "cover" }}
                      />
                    )}
                    <span>{option.name}</span>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To Country"
                  placeholder="Select country"
                />
              )}
              disableClearable={false}
            />
            {isOther(lens.prop("destinationCountry").get()?.id) && (
              <Collapse
                in={isOther(lens.prop("destinationCountry").get()?.id)}
              >
                <TextField
                  label="Specify destination country"
                  value={lens.prop("destinationCountryCustom").get() || ""}
                  onChange={(e) =>
                    lens.prop("destinationCountryCustom").set(e.target.value)
                  }
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </Collapse>
            )}
          </Box>

          {/* To City Filter */}
          <Box>
            <Autocomplete
              value={lens.prop("destinationCity").get() || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => {
                lens.prop("destinationCity").set(newValue);
              }}
              options={getOptionsWithOther(destinationCityList)}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To City"
                  placeholder="Select city"
                />
              )}
              disableClearable={false}
              disabled={!lens.prop("destinationCountry").get()}
            />
            {isOther(lens.prop("destinationCity").get()?.id) && (
              <Collapse in={isOther(lens.prop("destinationCity").get()?.id)}>
                <TextField
                  label="Specify destination city"
                  value={lens.prop("destinationCityCustom").get() || ""}
                  onChange={(e) =>
                    lens.prop("destinationCityCustom").set(e.target.value)
                  }
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </Collapse>
            )}
          </Box>

          {/* From Country Filter */}
        </Box>

        {/* Action buttons - now inside collapse */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            mt: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={save}
            fullWidth
            sx={{
              backgroundColor: theme.palette.courier.main,
              color: theme.palette.courier.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.courier.dark,
                transform: "translateY(-2px)",
                boxShadow: theme.shadows[2],
              },
              transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
            }}
          >
            Submit
          </Button>
          <Button
            variant="outlined"
            onClick={onClear}
            fullWidth
            sx={{
              borderColor: theme.palette.courier.main,
              color: theme.palette.courier.dark,
              "&:hover": {
                borderColor: theme.palette.courier.dark,
                backgroundColor: alpha(theme.palette.courier.main, 0.04),
              },
            }}
          >
            Clear
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}
